/**
 * Remembers which config the Configure page was left on so leaving the
 * page and coming back reopens it instead of the empty list (same idea
 * as Dashboards). The nav rail links at the bare `/configure` home, and
 * in-app Back links use `/configure?type=…` with no id; both are the
 * unified list. Visiting either list destination clears the remembered
 * detail so returning later stays on the list.
 *
 * The URL stays the only selection mechanism — this is a fallback
 * consulted when the user lands on Configure with nothing selected,
 * and the stored id is re-validated against the loaded rows before it
 * is restored so a deleted config falls back to the list.
 *
 * Read/write style mirrors `orchestrator-ui-persistence.ts`: namespaced
 * `ontology.*` keys, best-effort, never throws.
 */

import type { ConfigureType } from './ConfigurePage'

export interface LastVisitedConfig {
  type: ConfigureType
  id: string
}

export const LAST_VISITED_STORAGE_KEY = 'ontology.configure.lastVisited'

const CONFIGURE_TYPES = new Set<ConfigureType>(['custom', 'notification-rules'])

function hasLocalStorage(): boolean {
  return typeof window !== 'undefined' && 'localStorage' in window
}

export function readLastVisited(): LastVisitedConfig | null {
  if (!hasLocalStorage()) return null
  let raw: string | null = null
  try {
    raw = window.localStorage.getItem(LAST_VISITED_STORAGE_KEY)
  } catch {
    return null
  }
  if (raw === null) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const { type, id } = parsed as { type?: unknown; id?: unknown }
    if (typeof type !== 'string' || !CONFIGURE_TYPES.has(type as ConfigureType)) return null
    if (typeof id !== 'string' || id === '') return null
    return { type: type as ConfigureType, id }
  } catch {
    return null
  }
}

export function writeLastVisited(selection: LastVisitedConfig | null): void {
  if (!hasLocalStorage()) return
  try {
    if (selection === null || selection.id === '') {
      window.localStorage.removeItem(LAST_VISITED_STORAGE_KEY)
      return
    }
    window.localStorage.setItem(LAST_VISITED_STORAGE_KEY, JSON.stringify(selection))
  } catch {
    // best-effort; persistence is a convenience, not a correctness requirement
  }
}

export function hrefForLastVisited(selection: LastVisitedConfig): string {
  const params = new URLSearchParams({ type: selection.type, id: selection.id })
  return `/configure?${params.toString()}`
}
